<template>
  <div class="main-swiper" v-if="props.length">
    <div class="row-1 intro-title">
      <div
        v-if="props.title"
        class="main-swiper__title title-big3 is-medium-bold m-bot-10"
        :class="{
          'p-bot-5': props.type === 'custom-text-position',
        }"
        v-html="props.title"
      />
      <div
        v-if="props.text && props.type != 'custom-text-position'"
        class="main-swiper__text title-base m-bot-11"
        v-html="props.text"
      />
    </div>
    <swiper
      ref="swiper"
      class="slider-swiper"
      :class="{
        '--calculated-padding': paddingLeft - rowLimit > 0,
        [props.color]: true,
      }"
      :style="{
        '--padding-left': `${paddingLeft}px`,
      }"
      :navigation="{
        nextEl: nextButton,
        prevEl: prevButton,
      }"
      :scrollbar="{
        hide: true,
        draggable: true,
      }"
      :modules="[Pagination, A11y, Navigation, Scrollbar]"
      :loop="props.options?.loop"
      :slides-per-group="props.options?.slidesPerGroup || 1"
      :slides-per-view="props.options?.slidesPerView || 'auto'"
      :initial-slide="props.options?.initialSlide || 0"
      :space-between="props.options?.spaceBetween || 0"
      :lazy="true"
      @swiper="onSwiper"
      @realIndexChange="changeRealIndex"
    >
      <swiper-slide
        v-for="index in props.length"
        :key="index"
        :class="{ '--last-slide': index === props.length }"
      >
        <div class="slider-swiper__slide__wrap">
          <slot :name="`slide-${index - 1}`" />
        </div>
      </swiper-slide>

      <!-- Navigation-->
      <div
        class="swiper-buttons"
        :class="{
          '--calculated-padding-right': paddingRight - rowLimit > 0,
        }"
        :style="{
          '--padding-right': `${paddingRight}px`,
        }"
        v-if="props.length > 1 && !props.options?.hideButtons"
      >
        <button
          class="swiper-button-prev"
          ref="prevButton"
          aria-label="Button Prev"
        />
        <!-- Per la Timeline, non è il massimo -->
        <div
          v-show="props.actualYear"
          class="mobile-actual-year hide-for-large"
        >
          {{ props.actualYear }}
        </div>
        <button
          class="swiper-button-next"
          ref="nextButton"
          aria-label="Button Next"
        />
      </div>
      <div
        v-if="props.text && props.type === 'custom-text-position'"
        class="m-top-3 flex --justify-start text-button-section"
      >
        <div
          v-if="props.text"
          class="main-swiper__text title-base m-bot-11"
          v-html="props.text"
        />
        <nuxt-link
          v-if="props.bottoneCarosello"
          class="round-icon-btn"
          :to="localePath(props.bottoneCarosello.linkDefault)"
        >
          <span
            class="text-medium5 is-medium-bold"
            v-html="props.bottoneCarosello.label"
          />
        </nuxt-link>
      </div>
    </swiper>
  </div>
</template>

<script>
export default {
  name: 'WidgetSliderSwiper',
}
</script>

<script setup>
import { Pagination, A11y, Navigation, Scrollbar } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

const emit = defineEmits(['updateRealIndex', 'init'])

const props = defineProps({
  length: {
    type: Number,
    default: 0,
  },

  options: {
    type: Object,
  },

  title: {
    type: String,
    default: '',
  },

  text: {
    type: String,
    default: '',
  },

  color: {
    type: String,
    default: '',
  },

  bottoneCarosello: {
    type: Object,
  },

  actualYear: {
    type: String,
    default: '',
  },

  type: {
    type: String,
    default: '',
  },
})

const slider = ref(null)
const nextButton = ref(null)
const prevButton = ref(null)

const onSwiper = (swiper) => {
  slider.value = swiper
  emit('init', swiper)
}

const changeRealIndex = (swiper) => {
  emit('updateRealIndex', swiper.realIndex)
}

const paddingLeft = ref(0)
const paddingRight = ref(0)
const rowLimit = 200
const paddingLimit = 1920

const onResize = () => {
  paddingLeft.value = Math.round((window.innerWidth - paddingLimit) / 2)
  paddingRight.value = Math.round((window.innerWidth - paddingLimit) / 2)
}

const resizeObservers = ref([])

const setMaxSlideHeight = (slides) => {
  let maxHeight = 0

  // Calcola l'altezza maggiore tra tutte le slides
  slides.forEach((slide) => {
    // Altezza inziale di default
    slide.style.height = ''
    /*
      Se l'altezza della slide corrente è maggiore
      dell'altezza massima registrata, aggiorna maxHeight
    */
    if (slide.offsetHeight > maxHeight) {
      maxHeight = slide.offsetHeight
    }
  })

  // Imposta l'altezza di tutte le slides in base alla slide con l'altezza maggiore
  slides.forEach((slide) => {
    slide.style.height = `${maxHeight}px`
  })
}

// Disconnette gli observers creati
const cleanupObservers = () => {
  resizeObservers.value.forEach((observer) => observer.disconnect())
}

const onImageLoad = (slides) => {
  setMaxSlideHeight(slides)
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()

  // Per gestire più swiper in una pagina in modo indipendente
  const swiperContainers = document.querySelectorAll('.slider-swiper')
  const resizeObservers = []

  // Identifica ogni swiper con un indice univoco
  swiperContainers.forEach((swiper, index) => {
    swiper.classList.add(`index--${index}`)

    // Seleziona tutte le slides all'interno dello swiper corrente
    const slides = swiper.querySelectorAll('.content-block__swiper__slide')

    // Crea un ResizeObserver per monitorare i cambiamenti di dimensione delle slide
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        setMaxSlideHeight(slides)
      })
    })

    // Osserva ogni slide per i cambiamenti di dimensione
    slides.forEach((slide) => {
      resizeObserver.observe(slide)
    })

    // Osserva ogni immagine
    const images = swiper.querySelectorAll('img')
    images.forEach((img) => {
      img.addEventListener('load', () => onImageLoad(slides))
    })

    // Imposta l'altezza delle slides
    setMaxSlideHeight(slides)

    // Aggiungi l'observer all'array
    resizeObservers.push(resizeObserver)
  })

  onBeforeUnmount(() => {
    cleanupObservers()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss">
@import './style.scss';
</style>
